import { NEW_ITEM_INFO } from 'components/Sell/utils/common'
import Decimal from 'decimal.js'
import { cloneDeep, sum } from 'lodash-es'
import {
    DiscountBuyItemFrom,
    DiscountContract,
    DiscountGetDiscountType,
    DiscountGetItemFrom,
    DiscountMinimumRequirement,
    DiscountType,
} from 'models'
import {
    OrderFulfilmentType,
    OrderItemViewModel,
} from 'store/models/sellModels'
import generalDiscountFilter from 'views/Sale/discount/discountMapper/discountFilter/discountGeneralFilter'
import {
    BaseOrderItem,
    sumItemAmount,
} from 'views/Sale/utils/orderCalculation/common'

export interface BuyXGetYResult {
    buyXs: OrderItemViewModel[]
    discount: DiscountContract

    /** number of new promotion item get from processed BOGO result */
    additionalGetQty: number
}

/**
 * map items and bogo discount and calculate get quantity
 */
export const mapRequiredItemWithDiscount = (
    selectedItems: OrderItemViewModel[] = [],
    selectedDiscounts: DiscountContract[] = [],
    orderItems: OrderItemViewModel[] = [],
    currentFulfilmentType: OrderFulfilmentType,
    excludeDiscountIds: string[] = [],
    orderChannelId: number,
) => {
    const buyXGetYDiscounts = selectedDiscounts.filter(
        (d) => d.type === DiscountType.BuyXGetY,
    )
    const discountMapWithX: BuyXGetYResult[] = buyXGetYDiscounts
        .map((discount) => {
            if (
                !generalDiscountFilter(
                    discount,
                    currentFulfilmentType,
                    excludeDiscountIds,
                    orderChannelId,
                )
            )
                return {
                    buyXs: [],
                    discount,
                    additionalGetQty: 0,
                }
            //get the number usage of the discount
            const proItems = orderItems.filter(
                (lineItem) =>
                    // A line item which's already applied current discount
                    (lineItem.discounts?.some(
                        (a) => a.discountID === discount.discountID,
                    ) &&
                        //promotion items must be same as purchase in case
                        (discount.getAnyItemFrom !==
                            DiscountGetItemFrom.SameAsPurchasedProduct ||
                            selectedItems.some(
                                (s) => s.item.itemId === lineItem.item.itemId,
                            ))) ||
                    selectedItems.length === 0,
            )

            const requiredItems = getMatchRequiredItems(discount, selectedItems)

            const otherRequiredItems =
                discount.getAnyItemFrom ===
                DiscountGetItemFrom.SameAsPurchasedProduct
                    ? []
                    : getMatchRequiredItems(
                          discount,
                          // Find other items which is not part of `selectedItems` and not yet applied BOGO discount
                          orderItems.filter(
                              (lineItem) =>
                                  !selectedItems.some(
                                      (selectItem) => lineItem === selectItem,
                                  ) && !isPromotionItem(lineItem),
                          ),
                      )

            const ableAmountToGetY =
                calReceivedItemQuantity(otherRequiredItems, discount) -
                sumItemAmount(proItems)

            let amountNewPromotionItem = calReceivedItemQuantity(
                requiredItems,
                discount,
            )
            //if still has other able bogo items so it will able add new bogo
            //other wise will deduct able to add by current bogo discount
            amountNewPromotionItem =
                ableAmountToGetY >= 0
                    ? amountNewPromotionItem
                    : amountNewPromotionItem + ableAmountToGetY
            //able add promotion items not enought for the discount get quantity
            if (
                amountNewPromotionItem > 0 &&
                amountNewPromotionItem < (discount.getQty ?? 0)
            )
                amountNewPromotionItem = 0

            return {
                buyXs: requiredItems,
                discount,
                additionalGetQty: amountNewPromotionItem,
            }
        })
        .filter((discount) => {
            return discount.additionalGetQty !== 0
        })
    return discountMapWithX
}

export const getMinimunRequiredQuantity = (
    discount: DiscountContract,
    price: number,
) => {
    if (
        discount.minRequirement ===
        DiscountMinimumRequirement.MinimumQuantityOfItems
    ) {
        return discount.minRequirementValue ?? 1
    } else if (
        discount.minRequirement ===
        DiscountMinimumRequirement.MinimumPurchaseAmount
    ) {
        return Math.floor(
            new Decimal(discount.minRequirementValue ?? 1)
                .div(price)
                .toNumber(),
        )
    }

    return 1
}

export const calReceivedItemQuantity = (
    buyItems: OrderItemViewModel[],
    discount: DiscountContract,
) => {
    if (buyItems.length === 0) return 0
    let receivedDiscountAmount = 1
    receivedDiscountAmount = sum(
        buyItems.map(
            (a) => a.amount / getMinimunRequiredQuantity(discount, a.lineTotal),
        ),
    )

    return Math.floor(receivedDiscountAmount) * (discount.getQty ?? 1)
}

/**
 * Usually made for specific case that GetItems (Y) are subset or partially part of BuyItems (X)
 * E.g., Buy 2 [Drink A or Drink B] -> Get 1 [Drink A or Drink B]
 * this will then used for processing in `addSelectedBogoItem`
 * @param discount
 * @param xItems
 * @param yItems
 * @returns
 */
export const findOverLapItems = (
    discount: DiscountContract,
    xItems: OrderItemViewModel[],
    yItems: OrderItemViewModel[],
) => {
    let xAbleToY: OrderItemViewModel[]
    let yAbleToX: OrderItemViewModel[]

    // Bypass overlap checking if GetItems(Y) is the same product
    if (discount.getAnyItemFrom === DiscountGetItemFrom.SameAsPurchasedProduct)
        return [] // unable to overlap because it need to same as purchased item

    // x should not has applied discount
    xAbleToY =
        discount.getAnyItemFrom === DiscountGetItemFrom.SelectedCategories
            ? xItems.filter(
                  (a) =>
                      (a.discounts?.length ?? 0) === 0 &&
                      discount.getIDs!.some((g) => g === a.item.categoryId),
              )
            : xItems.filter(
                  (a) =>
                      (a.discounts?.length ?? 0) === 0 &&
                      discount.getIDs!.some(
                          (g) => g === a.item.itemId || g === a.item.baseItemId,
                      ),
              )
    yAbleToX =
        discount.buyAnyItemFrom === DiscountBuyItemFrom.SelectedCategories
            ? yItems.filter((y) =>
                  discount.buyIDs!.some((d) => d === y.item.categoryId),
              )
            : yItems.filter((y) =>
                  discount.buyIDs!.some(
                      (b) => b === y.item.itemId || b === y.item.baseItemId,
                  ),
              )
    return cloneDeep([...yAbleToX, ...xAbleToY])
}
export const cloneOrderItem = (
    selectedItem: OrderItemViewModel,
    discounts: DiscountContract[],
    amount: number,
): OrderItemViewModel => ({
    ...NEW_ITEM_INFO(),
    ...selectedItem,
    id: `${selectedItem.item.itemId}_${new Date().valueOf()}`,
    amount,
    lineTotal: selectedItem.lineTotal,
    discounts,
})

/** determine whether the cart item is free item (got from BOGO) */
export const isFreeItem = (item: BaseOrderItem) => {
    return (item.discounts ?? []).some((discount) => isFreeDiscount(discount))
}

/** True if the cart item contains any BOGO Discount */
export const isPromotionItem = (item: BaseOrderItem | undefined) => {
    return (item?.discounts ?? []).some(
        (discount) => discount.type === DiscountType.BuyXGetY,
    )
}

/** find items with match with required condition*/
const getMatchRequiredItems = (
    discount: DiscountContract,
    selectItems: OrderItemViewModel[],
): OrderItemViewModel[] => {
    if (discount.buyAnyItemFrom === DiscountBuyItemFrom.SelectedCategories)
        return selectItems.filter((i) =>
            discount.buyIDs?.includes(i.item.categoryId),
        )
    else if (discount.buyAnyItemFrom === DiscountBuyItemFrom.SelectedProducts)
        return selectItems.filter(
            (i) =>
                discount.buyIDs?.includes(i.item.itemId) ||
                discount.buyIDs?.includes(i.item.baseItemId ?? ''),
        )
    return []
}
export const isFreeDiscount = (d: DiscountContract): boolean => {
    return (
        d.getAtDiscountType === DiscountGetDiscountType.Free &&
        d.type === DiscountType.BuyXGetY
    )
}
